/**
 * Template Field Detector Utility
 * Extracts Mustache template tokens/variables from HTML templates
 */

/**
 * Extracts all Mustache tokens from a template HTML string
 * @param {string} templateHTML - The HTML template with Mustache syntax
 * @returns {object} Object containing categorized tokens
 */
export const extractTemplateTokens = (templateHTML) => {
  if (!templateHTML) {
    return {
      variables: [],
      sections: [],
      conditionals: [],
      all: []
    };
  }

  const tokens = {
    variables: new Set(),      // {{variable}}
    sections: new Set(),        // {{#section}}...{{/section}}
    conditionals: new Set(),    // {{#if}}...{{/if}}
    all: new Set()
  };

  // Regex patterns for different Mustache syntax
  const patterns = {
    // Simple variables: {{name}}, {{email}}, etc.
    variable: /\{\{\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}/g,
    
    // Section start: {{#experience}}, {{#skills}}, etc.
    sectionStart: /\{\{#\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}/g,
    
    // Section end: {{/experience}}, {{/skills}}, etc.
    sectionEnd: /\{\{\/\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}/g,
    
    // Inverted sections: {{^section}}...{{/section}}
    invertedSection: /\{\{\^\s*([a-zA-Z_][a-zA-Z0-9_.]*)\s*\}\}/g
  };

  // Extract simple variables
  let match;
  while ((match = patterns.variable.exec(templateHTML)) !== null) {
    const token = match[1];
    // Skip section/conditional markers
    if (!token.startsWith('#') && !token.startsWith('/') && !token.startsWith('^')) {
      tokens.variables.add(token);
      tokens.all.add(token);
    }
  }

  // Extract sections
  while ((match = patterns.sectionStart.exec(templateHTML)) !== null) {
    const section = match[1];
    tokens.sections.add(section);
    tokens.all.add(section);
  }

  // Extract inverted sections
  while ((match = patterns.invertedSection.exec(templateHTML)) !== null) {
    const section = match[1];
    tokens.sections.add(section);
    tokens.all.add(section);
  }

  // Identify conditional helpers
  const conditionalKeywords = ['if', 'unless', 'each', 'with'];
  conditionalKeywords.forEach(keyword => {
    const regex = new RegExp(`\\{\\{#${keyword}\\s+([a-zA-Z_][a-zA-Z0-9_.]*)\\s*\\}\\}`, 'g');
    while ((match = regex.exec(templateHTML)) !== null) {
      const conditional = match[1];
      tokens.conditionals.add(conditional);
      tokens.all.add(conditional);
    }
  });

  // Convert Sets to Arrays for easier use
  return {
    variables: Array.from(tokens.variables).sort(),
    sections: Array.from(tokens.sections).sort(),
    conditionals: Array.from(tokens.conditionals).sort(),
    all: Array.from(tokens.all).sort(),
    simpleFields: Array.from(tokens.variables).sort(), // Alias for variables (for backward compatibility)
    
    // Helper methods
    hasToken: (tokenName) => tokens.all.has(tokenName),
    hasSection: (sectionName) => tokens.sections.has(sectionName),
    hasVariable: (variableName) => tokens.variables.has(variableName),
    
    // Summary stats
    stats: {
      totalTokens: tokens.all.size,
      variableCount: tokens.variables.size,
      sectionCount: tokens.sections.size,
      conditionalCount: tokens.conditionals.size
    }
  };
};

/**
 * Analyzes template structure and identifies required fields
 * @param {string} templateHTML - The HTML template
 * @returns {object} Required fields organized by category
 */
export const analyzeTemplateStructure = (templateHTML) => {
  const tokens = extractTemplateTokens(templateHTML);
  
  // Common field categorization
  const structure = {
    personalInfo: [],
    experience: false,
    education: false,
    skills: false,
    projects: false,
    certifications: false,
    customSections: []
  };

  // Personal info fields
  const personalInfoFields = ['name', 'email', 'phone', 'location', 'linkedin', 'website', 'github', 'portfolio'];
  personalInfoFields.forEach(field => {
    if (tokens.hasVariable(`personalInfo.${field}`) || tokens.hasVariable(field)) {
      structure.personalInfo.push(field);
    }
  });

  // Check for standard sections
  const standardSections = ['experience', 'education', 'skills', 'projects', 'certifications'];
  standardSections.forEach(section => {
    if (tokens.hasSection(section)) {
      structure[section] = true;
    }
  });

  // Identify custom sections (sections not in standard list)
  tokens.sections.forEach(section => {
    if (!standardSections.includes(section) && !personalInfoFields.includes(section)) {
      structure.customSections.push(section);
    }
  });

  return structure;
};

/**
 * Validates if resume data matches template requirements
 * @param {object} resumeData - The resume data object
 * @param {object} tokens - Template tokens from extractTemplateTokens
 * @returns {object} Validation result with missing fields
 */
export const validateResumeData = (resumeData, tokens) => {
  const missing = {
    variables: [],
    sections: []
  };

  const warnings = [];

  // Check required sections
  tokens.sections.forEach(section => {
    if (!resumeData[section] || (Array.isArray(resumeData[section]) && resumeData[section].length === 0)) {
      missing.sections.push(section);
      warnings.push(`Section '${section}' is empty or missing`);
    }
  });

  // Check critical personal info
  const criticalFields = ['name', 'email'];
  criticalFields.forEach(field => {
    const hasField = tokens.hasVariable(field) || tokens.hasVariable(`personalInfo.${field}`);
    if (hasField) {
      const value = resumeData.personalInfo?.[field] || resumeData[field];
      if (!value) {
        missing.variables.push(field);
        warnings.push(`Required field '${field}' is missing`);
      }
    }
  });

  return {
    isValid: missing.variables.length === 0 && missing.sections.length === 0,
    missing,
    warnings,
    completeness: calculateCompleteness(resumeData, tokens)
  };
};

/**
 * Calculates how complete the resume data is relative to template
 * @param {object} resumeData - The resume data
 * @param {object} tokens - Template tokens
 * @returns {number} Percentage (0-100) of completeness
 */
const calculateCompleteness = (resumeData, tokens) => {
  let filled = 0;
  let total = tokens.stats.totalTokens;

  if (total === 0) return 100;

  // Count filled variables
  tokens.variables.forEach(variable => {
    const parts = variable.split('.');
    let value = resumeData;
    
    for (const part of parts) {
      value = value?.[part];
    }
    
    if (value && value !== '') {
      filled++;
    }
  });

  // Count filled sections
  tokens.sections.forEach(section => {
    const data = resumeData[section];
    if (data && Array.isArray(data) && data.length > 0) {
      filled++;
    } else if (data && typeof data === 'object' && Object.keys(data).length > 0) {
      filled++;
    }
  });

  return Math.round((filled / total) * 100);
};

/**
 * Gets a list of all Mustache tokens in the template (for debugging)
 * @param {string} templateHTML - The HTML template
 * @returns {Array} Array of all token strings found
 */
export const getAllTokenStrings = (templateHTML) => {
  const allMatches = [];
  const regex = /\{\{[^}]+\}\}/g;
  let match;
  
  while ((match = regex.exec(templateHTML)) !== null) {
    allMatches.push(match[0]);
  }
  
  return allMatches;
};
